import { Injectable, inject } from '@angular/core';

import { mergeMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { AppleMdmService } from '../services/apple-mdm-service';
import { ManagedSettingsApiActions, ManagedSettingsCommandActions } from './managed-settings.actions';
import { safeMap } from '@era/stores';

@Injectable()
export class ManagedSettingsEffects {
  private readonly actions$ = inject(Actions);
  private readonly appleMdmService = inject(AppleMdmService);

  // Depending on the type of incoming managed setting, from here we sent
  // the corresponding API call.

  managedSettingsSetWallpaperEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagedSettingsCommandActions.setWallpaper),
      mergeMap((action) =>
        safeMap(
          this.appleMdmService.setWallpaper(action.wallpaper, action.udid),
          () => ManagedSettingsApiActions.setWallpaperSucceeded({ udid: action.udid }),
          (error) => ManagedSettingsApiActions.setWallpaperFailed({ udid: action.udid, error })
        )
      )
    );
  });

  managedSettingsDeviceNameEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagedSettingsCommandActions.deviceName),
      mergeMap((action) =>
        safeMap(
          this.appleMdmService.deviceName(action.udid, action.deviceName),
          () => ManagedSettingsApiActions.devicenameSucceeded({ udid: action.udid }),
          (error) => ManagedSettingsApiActions.devicenameFailed({ udid: action.udid, error })
        )
      )
    );
  });

  managedSettingsHostNameEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagedSettingsCommandActions.hostName),
      mergeMap((action) =>
        safeMap(
          this.appleMdmService.hostName(action.udid, action.hostName),
          () => ManagedSettingsApiActions.hostNameSucceeded({ udid: action.udid }),
          (error) => ManagedSettingsApiActions.hostNameFailed({ udid: action.udid, error })
        )
      )
    );
  });

  managedSettingsBluetoothEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagedSettingsCommandActions.bluetooth),
      mergeMap((action) =>
        safeMap(
          this.appleMdmService.bluetooth(action.udid, action.enabled),
          () => ManagedSettingsApiActions.bluetoothSucceeded({ udid: action.udid }),
          (error) => ManagedSettingsApiActions.bluetoothFailed({ udid: action.udid, error })
        )
      )
    );
  });

  managedSettingsDataRoamingEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagedSettingsCommandActions.dataRoaming),
      mergeMap((action) =>
        safeMap(
          this.appleMdmService.dataRoaming(action.udid, action.enabled),
          () => ManagedSettingsApiActions.dataRoamingSucceeded({ udid: action.udid }),
          (error) => ManagedSettingsApiActions.dataRoamingFailed({ udid: action.udid, error })
        )
      )
    );
  });

  managedSettingsVoiceRoamingEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagedSettingsCommandActions.voiceRoaming),
      mergeMap((action) =>
        safeMap(
          this.appleMdmService.voiceRoaming(action.udid, action.enabled),
          () => ManagedSettingsApiActions.voiceRoamingSucceeded({ udid: action.udid }),
          (error) => ManagedSettingsApiActions.voiceRoamingFailed({ udid: action.udid, error })
        )
      )
    );
  });

  managedSettingsPersonalHotspotEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagedSettingsCommandActions.personalHotspot),
      mergeMap((action) =>
        safeMap(
          this.appleMdmService.personalHotspot(action.udid, action.enabled),
          () => ManagedSettingsApiActions.personalHotspotSucceeded({ udid: action.udid }),
          (error) => ManagedSettingsApiActions.personalHotspotFailed({ udid: action.udid, error })
        )
      )
    );
  });

  managedSettingsApplicationAttributesEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagedSettingsCommandActions.applicationAttributes),
      mergeMap((action) =>
        safeMap(
          this.appleMdmService.applicationAttributes(action.udid, action.identifier, action.vpnuuid),
          () => ManagedSettingsApiActions.applicationsAttributesSucceeded({ udid: action.udid }),
          (error) => ManagedSettingsApiActions.applicationsAttributesFailed({ udid: action.udid, error })
        )
      )
    );
  });

  managedSettingsMdmOptionsEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagedSettingsCommandActions.mDMOptions),
      mergeMap((action) =>
        safeMap(
          this.appleMdmService.mdmOptions(action.udid, action.activationLockAllowedWhileSupervised),
          () => ManagedSettingsApiActions.mDMOptionsSucceeded({ udid: action.udid }),
          (error) => ManagedSettingsApiActions.mDMOptionsFailed({ udid: action.udid, error })
        )
      )
    );
  });

  managedSettingsPassCodeLockGracePeriodEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagedSettingsCommandActions.passCodeLockGracePeriod),
      mergeMap((action) =>
        safeMap(
          this.appleMdmService.passCodeLockGracePeriod(action.udid, action.passCodeLockGracePeriod),
          () => ManagedSettingsApiActions.passCodeLockGracePeriodSucceeded({ udid: action.udid }),
          (error) => ManagedSettingsApiActions.passCodeLockGracePeriodFailed({ udid: action.udid, error })
        )
      )
    );
  });

  managedSettingsMaximumResidentUsersEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagedSettingsCommandActions.maximumResidentUsers),
      mergeMap((action) =>
        safeMap(
          this.appleMdmService.maximumResidentUsers(action.udid, action.maximumResidentUsers),
          () => ManagedSettingsApiActions.maximumResidentUsersSucceeded({ udid: action.udid }),
          (error) => ManagedSettingsApiActions.maximumResidentUsersFailed({ udid: action.udid, error })
        )
      )
    );
  });

  managedSettingsDiagnosticSubmissionEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagedSettingsCommandActions.diagnosticSubmission),
      mergeMap((action) =>
        safeMap(
          this.appleMdmService.diagnosticSubmission(action.udid, action.enabled),
          () => ManagedSettingsApiActions.diagnosticSubmissionSucceeded({ udid: action.udid }),
          (error) => ManagedSettingsApiActions.diagnosticSubmissionFailed({ udid: action.udid, error })
        )
      )
    );
  });

  managedSettingsAppanalyticsEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ManagedSettingsCommandActions.appAnalytics),
      mergeMap((action) =>
        safeMap(
          this.appleMdmService.appAnalytics(action.udid, action.enabled),
          () => ManagedSettingsApiActions.appAnalyticsSucceeded({ udid: action.udid }),
          (error) => ManagedSettingsApiActions.appAnalyticsFailed({ udid: action.udid, error })
        )
      )
    );
  });
}
